'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_STEP_PX = 80;

/**
 * Tracks whether a horizontally scrollable element still has content to its
 * right, so callers can render a "scroll further" affordance.
 */
export function useHorizontalScrollAffordance<T extends HTMLElement>() {
  const scrollRef = useRef<T>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    update();
    el.addEventListener('scroll', update);
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(update);
    resizeObserver?.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      resizeObserver?.disconnect();
    };
  }, []);

  const scrollRight = useCallback(() => {
    scrollRef.current?.scrollBy({ left: SCROLL_STEP_PX, behavior: 'smooth' });
  }, []);

  return { scrollRef, canScrollRight, scrollRight };
}
